/**
 * DatePicker - Accessible date selection component
 * 
 * Design Principles:
 * - Semantic HTML with proper ARIA attributes
 * - Keyboard navigable
 * - Mobile-first responsive design
 * - Follows Figma design system
 * 
 * Features:
 * - Month/year navigation
 * - Selected date highlighting
 * - Today button for quick access
 * - Cancel action
 * - Prev/next day navigation
 */

import { useState, useEffect, useRef } from 'react';
import { CalendarIcon } from '#icons';
import { ChevronIcon } from '#icons';

export interface DatePickerProps {
  /** Currently selected date */
  selectedDate?: Date;
  /** Callback when date is selected */
  onDateSelect?: (date: Date) => void;
  /** Callback when date picker is cancelled */
  onCancel?: () => void;
  /** Show the full calendar view */
  showCalendar?: boolean;
  /** Minimum selectable date (optional, defaults to today) */
  minDate?: Date;
  /** Maximum selectable date (optional). Dates after this are disabled. */
  maxDate?: Date;
  /**
   * Web portal booking-calendar mode. Renders the open calendar as a floating
   * popover (absolutely positioned over the content below, with
   * outside-click-to-dismiss) instead of an inline block that pushes content
   * down, and applies the mobile calendar design (rounded cells, bordered
   * month-nav, divider above full-width footer buttons) at every width rather
   * than reverting to the compact desktop treatment. Defaults to false so
   * existing consumers (e.g. admin filter panels) keep the inline layout and
   * the desktop reversion.
   */
  floatingCalendar?: boolean;
  /** Additional CSS classes */
  className?: string;
}

export function DatePicker({
  selectedDate = new Date(),
  onDateSelect,
  onCancel,
  showCalendar = false,
  minDate,
  maxDate,
  floatingCalendar = false,
  className = '',
}: DatePickerProps) {
  const [currentDate, setCurrentDate] = useState(selectedDate);
  const [viewDate, setViewDate] = useState(selectedDate);
  const [isCalendarOpen, setIsCalendarOpen] = useState(showCalendar);
  const containerRef = useRef<HTMLDivElement>(null);

  // Default minDate to today if not provided
  const effectiveMinDate = minDate || new Date();

  // Sync internal state when selectedDate prop changes
  // This allows parent components (like BookingContext) to control the selected date
  //
  // Guarded by VALUE, not identity: callers pass `parseISO(...)` — a fresh Date
  // object every render — so an identity-keyed effect fired on every parent
  // re-render and snapped `viewDate` back to the selected month while the user
  // (or an e2e run) was paging the calendar toward another one.
  const lastSelectedTime = useRef(selectedDate?.getTime());
  useEffect(() => {
    if (selectedDate?.getTime() === lastSelectedTime.current) return;
    lastSelectedTime.current = selectedDate?.getTime();
    setCurrentDate(selectedDate);
    setViewDate(selectedDate);
  }, [selectedDate]);

  // Floating popover: dismiss when clicking outside the picker.
  useEffect(() => {
    if (!floatingCalendar || !isCalendarOpen) return;
    const handleOutsideClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsCalendarOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [floatingCalendar, isCalendarOpen]);

  // Format date for display
  const formatDateDisplay = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  // Get month name
  const getMonthName = (date: Date): string => {
    return date.toLocaleDateString('en-US', { month: 'long' });
  };

  // Navigate to previous day
  const handlePrevDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 1);
    setCurrentDate(newDate);
    onDateSelect?.(newDate);
  };

  // Navigate to next day
  const handleNextDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 1);
    setCurrentDate(newDate);
    onDateSelect?.(newDate);
  };

  /**
   * Move the calendar one month, keeping the day out of it.
   *
   * `setMonth` does NOT clamp: it overflows into the following month when the target
   * month is too short for the current day-of-month. With `viewDate` on the 31st,
   * `setMonth(+1)` onto a 30-day month yields the 1st of the month AFTER — so on 31
   * August one "next month" click landed on October and September became unreachable,
   * with the button then disabled because October is past the booking window.
   *
   * Building the date from (year, month, 1) sidesteps it entirely: only the month is
   * ever carried, which is all the header and the grid read from `viewDate` anyway.
   * The same overflow applies going back (31 March → 3 March under DST-free months),
   * so both directions go through here.
   */
  const shiftViewMonth = (delta: number) => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + delta, 1));
  };

  // Navigate to previous month
  const handlePrevMonth = () => shiftViewMonth(-1);

  // Navigate to next month
  const handleNextMonth = () => shiftViewMonth(1);

  // Handle date selection
  const handleDateClick = (date: Date) => {
    setCurrentDate(date);
    setIsCalendarOpen(false);
    onDateSelect?.(date);
  };

  // Handle today button
  const handleToday = () => {
    const today = new Date();
    setCurrentDate(today);
    setViewDate(today);
    setIsCalendarOpen(false);
    onDateSelect?.(today);
  };

  // Handle cancel
  const handleCancel = () => {
    setIsCalendarOpen(false);
    onCancel?.();
  };

  // Get calendar days for current month
  const getCalendarDays = () => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    
    // First day of the month
    const firstDay = new Date(year, month, 1);
    const startingDayOfWeek = firstDay.getDay(); // 0 = Sunday
    
    // Last day of the month
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    // Previous month's days to fill the grid
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    
    const days: (Date | null)[] = [];
    
    // Add previous month's trailing days
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push(new Date(year, month - 1, prevMonthLastDay - i));
    }
    
    // Add current month's days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    
    // Complete only the final week rather than padding to a fixed 6 rows:
    // 35 cells (5 rows) when the month fits, 42 (6 rows) when it spills over.
    // Avoids a trailing all-next-month row and matches the Figma (December
    // renders as 5 rows).
    const totalCells = Math.ceil(days.length / 7) * 7;
    const remainingDays = totalCells - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push(new Date(year, month + 1, i));
    }
    
    return days;
  };

  const calendarDays = getCalendarDays();
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Check if two dates are the same day
  const isSameDay = (date1: Date, date2: Date): boolean => {
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  };

  // Check if date is in current viewing month
  const isCurrentMonth = (date: Date): boolean => {
    return date.getMonth() === viewDate.getMonth();
  };

  // Check if date is today
  const isToday = (date: Date): boolean => {
    return isSameDay(date, new Date());
  };

  // Check if date is before minimum date (today by default)
  const isBeforeMinDate = (date: Date): boolean => {
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const minDateOnly = new Date(
      effectiveMinDate.getFullYear(),
      effectiveMinDate.getMonth(),
      effectiveMinDate.getDate()
    );
    return dateOnly < minDateOnly;
  };

  // Check if date is after maximum date (no cap by default)
  const isAfterMaxDate = (date: Date): boolean => {
    if (!maxDate) return false;
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const maxDateOnly = new Date(
      maxDate.getFullYear(),
      maxDate.getMonth(),
      maxDate.getDate()
    );
    return dateOnly > maxDateOnly;
  };

  // Check if previous day button should be disabled
  const isPrevDayDisabled = (): boolean => {
    const prevDate = new Date(currentDate);
    prevDate.setDate(prevDate.getDate() - 1);
    return isBeforeMinDate(prevDate);
  };

  // Check if next day button should be disabled
  const isNextDayDisabled = (): boolean => {
    const nextDate = new Date(currentDate);
    nextDate.setDate(nextDate.getDate() + 1);
    return isAfterMaxDate(nextDate);
  };

  // Check if previous month button should be disabled
  const isPrevMonthDisabled = (): boolean => {
    // Disable if all days in previous month would be before minDate
    const firstDayOfCurrentMonth = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
    return isBeforeMinDate(firstDayOfCurrentMonth);
  };

  // Check if next month button should be disabled
  const isNextMonthDisabled = (): boolean => {
    // Disable if all days in next month would be after maxDate
    const firstDayOfNextMonth = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1);
    return isAfterMaxDate(firstDayOfNextMonth);
  };

  return (
    <div ref={containerRef} className={`${floatingCalendar ? 'relative' : ''} ${className}`}>
      {/* Date Selector with Navigation - Pill-shaped, left-aligned on desktop */}
      <div className="inline-flex w-full items-center gap-2 rounded-full bg-bg-secondary px-4 py-2.5 sm:w-auto sm:px-6">
        <button
          type="button"
          onClick={handlePrevDay}
          disabled={isPrevDayDisabled()}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-gray-300 text-theme-600 transition-colors hover:border-theme-600 hover:bg-theme-50 focus:outline-none focus:ring-2 focus:ring-theme-600 focus:ring-offset-2 disabled:border-fg-muted disabled:text-fg-muted disabled:cursor-not-allowed disabled:opacity-50"
          aria-label="Previous date"
          data-testid="prev-day-button"
        >
          <ChevronIcon ariaHidden className="h-3 w-3 rotate-180" />
        </button>

        <button
          type="button"
          onClick={() => setIsCalendarOpen(!isCalendarOpen)}
          className="flex flex-1 items-center gap-2 px-2 text-sm font-medium text-gray-900 transition-colors hover:text-theme-700 focus:outline-none focus:ring-2 focus:ring-theme-600 focus:ring-offset-2 rounded-md"
          data-testid="current-date-button"
        >
          <CalendarIcon ariaHidden className="h-4 w-4 text-fg-primary" />
          <span className="whitespace-nowrap text-fg-quaternary">{formatDateDisplay(currentDate)}</span>
        </button>

        <button
          type="button"
          onClick={handleNextDay}
          disabled={isNextDayDisabled()}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-theme-600 text-theme-600 transition-colors hover:bg-theme-50 focus:outline-none focus:ring-2 focus:ring-theme-600 focus:ring-offset-2 disabled:border-fg-muted disabled:text-fg-muted disabled:cursor-not-allowed disabled:opacity-50"
          aria-label="Next date"
          data-testid="next-day-button"
        >
          <ChevronIcon ariaHidden className="h-3 w-3" />
        </button>
      </div>

      {/* Calendar View */}
      {isCalendarOpen && (
        <div
        className={
          floatingCalendar
            ? 'absolute left-0 right-0 top-full z-50 mt-2 rounded-3xl border border-gray-200 bg-white p-4 shadow-lg sm:right-auto sm:w-96'
            : 'mt-4 rounded-3xl border border-gray-200 bg-white p-4 shadow-lg sm:rounded-lg sm:p-6'
        }
        data-testid="date-picker-calendar">
          {/* Month Navigation */}
          <div className={`mb-2 flex items-center justify-between ${floatingCalendar ? '' : 'sm:mb-4'}`}>
            <button
              type="button"
              onClick={handlePrevMonth}
              disabled={isPrevMonthDisabled()}
              className={`flex size-8 items-center justify-center rounded-full border border-gray-300 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-theme-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-30 ${floatingCalendar ? '' : 'sm:border-0'}`}
              aria-label="Previous month"
              data-testid="date-picker_prev-month-button"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <h3 
            className="text-lg font-semibold text-gray-900" 
            data-testid="date-picker-month">
              {getMonthName(viewDate)}
            </h3>

            <button
              type="button"
              onClick={handleNextMonth}
              disabled={isNextMonthDisabled()}
              className={`flex size-8 items-center justify-center rounded-full border border-theme-600 text-theme-600 transition-colors hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-theme-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-30 ${floatingCalendar ? '' : 'sm:border-0 sm:text-gray-400'}`}
              aria-label="Next month"
              data-testid="date-picker_next-month-button"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Week Days Header */}
          <div className="mb-2 grid grid-cols-7 gap-1">
            {weekDays.map((day) => (
              <div
                key={day}
                className="text-center text-xs font-medium text-gray-500"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((date, index) => {
              const isSelected = date && isSameDay(date, currentDate);
              const isTodayDate = date && isToday(date);
              const isInCurrentMonth = date && isCurrentMonth(date);
              const isPastDate = date ? isBeforeMinDate(date) : false;
              const isFutureDate = date ? isAfterMaxDate(date) : false;
              const isOutOfRange = isPastDate || isFutureDate;

              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => date && !isOutOfRange && handleDateClick(date)}
                  disabled={!date || isOutOfRange}
                  data-testid={date ? `date-picker_date-${date.getDate()}-${date.getMonth()}` : undefined}
                  className={`
                    h-9 w-full rounded-2xl text-sm font-medium transition-all ${floatingCalendar ? '' : 'sm:h-10 sm:rounded-full'}
                    focus:outline-none focus:ring-2 focus:ring-theme-600 focus:ring-offset-2
                    disabled:cursor-not-allowed

                    ${isSelected
                      ? 'bg-theme-700 text-white shadow-md hover:bg-theme-600'
                      : isTodayDate
                      ? 'border-2 border-theme-600 text-theme-700 hover:bg-theme-50'
                      : isOutOfRange
                      ? 'text-gray-300 line-through cursor-not-allowed'
                      : isInCurrentMonth
                      ? 'text-gray-900 hover:bg-gray-100'
                      : 'text-gray-400 hover:bg-gray-50'
                    }
                  `}
                  aria-label={date ? formatDateDisplay(date) : undefined}
                  aria-current={isSelected ? 'date' : undefined}
                  aria-disabled={isOutOfRange || undefined}
                >
                  {date ? date.getDate() : ''}
                </button>
              );
            })}
          </div>

          {/* Action Buttons — mobile gets a divider above (per Figma); desktop unchanged */}
          <div className={`mt-6 flex items-center gap-3 border-t border-gray-100 pt-5 ${floatingCalendar ? '' : 'sm:justify-between sm:gap-0 sm:border-t-0 sm:pt-0'}`}>
            <button
              type="button"
              onClick={handleCancel}
              className={`flex-1 rounded-md px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-theme-600 focus:ring-offset-2 ${floatingCalendar ? '' : 'sm:flex-none'}`}
              data-testid="date-picker_cancel-button"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleToday}
              className={`flex-1 rounded-full border-2 border-theme-600 bg-white px-6 py-2 text-sm font-medium text-gray-900 transition-colors hover:bg-theme-50 focus:outline-none focus:ring-2 focus:ring-theme-600 focus:ring-offset-2 ${floatingCalendar ? '' : 'sm:flex-none sm:text-theme-700'}`}
              data-testid="date-picker_today-button"
            >
              Today
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
